import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getClockinEvents, postClockinEvent, deleteClockinEvent, generateEventQr,
  postClockinManual, getClockinReport, getStaff, getClasses, getSubjects, getTimetable
} from '@/api/owner';
import { QRCodeCanvas } from 'qrcode.react';
import { Plus, Trash2, Calendar, Clock, ShieldAlert, X, Users, CheckCircle2, AlertCircle } from 'lucide-react';

const arr = <T,>(s: any): T[] => {
  if (!s) return [];
  if (Array.isArray(s)) return s;
  if (Array.isArray(s.data)) return s.data;
  if (Array.isArray(s.data?.data)) return s.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;

export function ClockInPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'events' | 'manual' | 'monitor' | 'rules'>('events');
  const [showCreate, setShowCreate] = useState(false);
  const [activeEventQr, setActiveEventQr] = useState<any | null>(null);
  
  // Custom Toast & Confirm State
  const [toast, setToast] = useState<{type: 'success'|'error', msg: string} | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  React.useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const eventsQ = useQuery({ queryKey: ['clockin-events'], queryFn: () => getClockinEvents().then(unwrap) });
  const events = arr<any>(eventsQ.data);

  const staffQ = useQuery({ queryKey: ['owner-staff'], queryFn: () => getStaff().then(unwrap) });
  const staff = arr<any>(staffQ.data);

  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses().then(unwrap) });
  const subjectsQ = useQuery({ queryKey: ['owner-subjects'], queryFn: () => getSubjects().then(unwrap) });

  const deleteM = useMutation({
    mutationFn: (id: number) => deleteClockinEvent(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clockin-events'] }); setDeleteTarget(null); setToast({type: 'success', msg: 'Event deleted successfully.'}); },
    onError: () => { setDeleteTarget(null); setToast({type: 'error', msg: 'Failed to delete event.'}); }
  });

  const generateQrM = useMutation({
    mutationFn: (id: number) => generateEventQr(id),
    onSuccess: (res: any) => {
      setActiveEventQr(unwrap(res));
      qc.invalidateQueries({ queryKey: ['clockin-events'] });
      setToast({type: 'success', msg: 'QR Code generated.'});
    },
    onError: () => setToast({type: 'error', msg: 'Failed to generate QR.'})
  });

  return (
    <div className="max-w-[1200px] mx-auto p-6 space-y-6 relative">
      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-[200] animate-slide-in">
          <div className={`rounded-xl shadow-2xl p-4 flex items-start gap-3 ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
            {toast.type === 'success' ? <CheckCircle2 className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
            <p className="text-sm font-semibold flex-1">{toast.msg}</p>
            <button onClick={() => setToast(null)}><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">PEOPLE & ACCESS</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Clock-in</h1>
          <p className="text-slate-500 text-sm mt-1">Manage QR events, lateness rules, and attendance monitoring</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-5 py-2.5 text-sm font-bold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-2 shadow-sm transition-colors">
          <Plus className="w-4 h-4" /> Create Event
        </button>
      </div>

      <div className="flex border-b border-slate-200 gap-2 overflow-x-auto">
        <button onClick={() => setTab('events')} className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${tab === 'events' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>QR Events</button>
        <button onClick={() => setTab('manual')} className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${tab === 'manual' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>Manual Clock-in</button>
        <button onClick={() => setTab('rules')} className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${tab === 'rules' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>Rules & Deductions</button>
        <button onClick={() => setTab('monitor')} className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${tab === 'monitor' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>Live Monitor</button>
      </div>

      {tab === 'events' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            {eventsQ.isLoading ? <p className="text-sm text-slate-500">Loading events...</p> : events.length === 0 ? (
              <div className="p-12 text-center bg-white border border-slate-200 rounded-xl shadow-sm">
                <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="font-semibold text-slate-700">No QR events created yet</p>
                <p className="text-sm text-slate-500 mt-1">Create an event to generate a QR code.</p>
              </div>
            ) : (
              events.map((e: any) => (
                <div key={e.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
                      {e.name}
                      <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full uppercase">{e.type}</span>
                    </h3>
                    <p className="text-sm text-slate-500 flex items-center gap-4 mt-1">
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {e.starts_at} - {e.ends_at}</span>
                      {e.school_class && <span>Class: {e.school_class.name}</span>}
                      {e.subject && <span>Subject: {e.subject.name}</span>}
                    </p>
                    <div className="flex gap-4 mt-2 text-xs font-semibold">
                      <span className="text-amber-600 bg-amber-50 px-2 py-1 rounded-md border border-amber-100">Late: ₦{Number(e.late_deduction).toLocaleString()} after {e.late_after_minutes}m</span>
                      <span className="text-red-600 bg-red-50 px-2 py-1 rounded-md border border-red-100">Absence: ₦{Number(e.absence_deduction).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => generateQrM.mutate(e.id)} className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-bold hover:bg-indigo-100 transition-colors">Show QR</button>
                    <button onClick={() => setDeleteTarget(e)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-5 h-5" /></button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="md:col-span-1 bg-slate-50/50 rounded-xl border border-slate-200 p-6 h-fit shadow-sm sticky top-6 text-center">
            <h3 className="font-extrabold text-slate-900 mb-4">Active QR Code</h3>
            {activeEventQr ? (
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col items-center">
                <QRCodeCanvas value={activeEventQr.qr_text} size={200} />
                <p className="text-xs text-slate-500 mt-3 font-mono">{activeEventQr.qr_text}</p>
                <p className="text-xs text-slate-400 mt-1">Expires: {new Date(activeEventQr.valid_until).toLocaleTimeString()}</p>
                <button onClick={() => setActiveEventQr(null)} className="mt-4 text-sm font-bold text-red-600 hover:underline">Close QR</button>
              </div>
            ) : (
              <div className="py-12 text-slate-400">
                <ShieldAlert className="w-10 h-10 mx-auto mb-2" />
                <p className="text-sm">Select an event and click "Show QR"</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'manual' && <ManualTab staff={staff} events={events} setToast={setToast} />}
      {tab === 'rules' && <RulesTab events={events} />}
      {tab === 'monitor' && <MonitorTab events={events} />}

      {showCreate && (
        <CreateEventModal 
          classes={arr(classesQ.data)} 
          subjects={arr(subjectsQ.data)} 
          onClose={() => setShowCreate(false)} 
          onSuccess={(newEvent: any) => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['clockin-events'] }); setToast({type: 'success', msg: 'Event created successfully.'}); if (newEvent?.id) generateQrM.mutate(newEvent.id); }} 
          setToast={setToast}
        />
      )}

      {/* Custom Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="font-bold text-lg text-slate-900 mb-2">Delete Event?</h3>
            <p className="text-sm text-slate-500 mb-6">Are you sure you want to delete <b>{deleteTarget.name}</b>? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-lg font-bold text-sm">Cancel</button>
              <button onClick={() => deleteM.mutate(deleteTarget.id)} className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-bold text-sm">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ManualTab({ staff, events, setToast }: { staff: any[]; events: any[]; setToast: any }) {
  const [eventId, setEventId] = useState('');
  const [userId, setUserId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState('on_time');

  const save = async () => {
    if (!eventId || !userId) return setToast({type: 'error', msg: 'Select event and staff'});
    try {
      await postClockinManual({ event_id: eventId, user_id: userId, date, clock_in: time, status, note });
      setToast({type: 'success', msg: 'Manual attendance recorded!'});
      setUserId(''); setTime('');
    } catch (e: any) { setToast({type: 'error', msg: e?.response?.data?.message ?? 'Failed'}); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:col-span-2 bg-white rounded-xl border border-slate-200 p-6 space-y-5 shadow-sm">
        <h2 className="text-lg font-bold mb-4">Manual Clock-in</h2>
        <div className="grid grid-cols-2 gap-5">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Event *</label>
            <select value={eventId} onChange={e => setEventId(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
              <option value="">Select Event</option>
              {events.map((e: any) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Staff *</label>
            <select value={userId} onChange={e => setUserId(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
              <option value="">Select Staff</option>
              {staff.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-5">
          <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Date</label><input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
          <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Time In</label><input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
              <option value="on_time">On Time</option><option value="late">Late</option><option value="absent">Absent</option><option value="excused">Excused</option>
            </select>
          </div>
        </div>
        <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Reason / Note</label><input value={note} onChange={e => setNote(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" placeholder="e.g. Phone battery died" /></div>
        <button onClick={save} className="w-full mt-6 py-3 bg-[#0f172a] text-white rounded-lg font-bold text-sm shadow-sm hover:bg-slate-800">Save Entry</button>
      </div>
    </div>
  );
}

function RulesTab({ events }: { events: any[] }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900 mb-1">Attendance Rules & Deductions</h2>
        <p className="text-sm text-slate-500">These rules apply when staff scan the QR code. Deductions are automatically synced to their Payroll profile.</p>
      </div>
      <div className="space-y-4">
        {events.map((e: any) => (
          <div key={e.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50 flex flex-col md:flex-row justify-between md:items-center gap-4">
            <div>
              <h3 className="font-bold text-slate-900">{e.name}</h3>
              <p className="text-xs text-slate-500 mt-1">Class: {e.school_class?.name || 'All'} · Subject: {e.subject?.name || 'General'}</p>
            </div>
            <div className="flex gap-3">
              <div className="px-3 py-2 bg-white rounded-lg border border-slate-200 shadow-sm text-center">
                <p className="text-[10px] font-bold text-slate-500 uppercase">Grace Period</p>
                <p className="font-bold text-slate-900">{e.late_after_minutes} mins</p>
              </div>
              <div className="px-3 py-2 bg-white rounded-lg border border-slate-200 shadow-sm text-center">
                <p className="text-[10px] font-bold text-amber-600 uppercase">Late Fine</p>
                <p className="font-bold text-slate-900">₦{Number(e.late_deduction).toLocaleString()}</p>
              </div>
              <div className="px-3 py-2 bg-white rounded-lg border border-slate-200 shadow-sm text-center">
                <p className="text-[10px] font-bold text-red-600 uppercase">Absence Fine</p>
                <p className="font-bold text-slate-900">₦{Number(e.absence_deduction).toLocaleString()}</p>
              </div>
            </div>
          </div>
        ))}
        {events.length === 0 && <p className="text-sm text-slate-500 italic">No events created yet. Create an event to set rules.</p>}
      </div>
    </div>
  );
}

function MonitorTab({ events }: { events: any[] }) {
  const [selectedEvent, setSelectedEvent] = useState<any | null>(events[0] || null);
  
  const reportQ = useQuery({ 
    queryKey: ['clockin-report', selectedEvent?.id], 
    queryFn: () => getClockinReport({ event_id: selectedEvent.id }).then(unwrap),
    enabled: !!selectedEvent 
  });

  const report = arr<any>(reportQ.data?.staff || reportQ.data);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:col-span-1 bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-2">
        <h3 className="font-bold text-slate-900 mb-3 px-2">Events</h3>
        {events.map((e: any) => (
          <button key={e.id} onClick={() => setSelectedEvent(e)} className={`w-full text-left px-4 py-3 rounded-lg text-sm font-bold transition-colors ${selectedEvent?.id === e.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50 text-slate-700'}`}>
            {e.name}
          </button>
        ))}
      </div>

      <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h3 className="font-bold text-slate-900 text-lg mb-6 flex items-center gap-2">
          <Users className="w-5 h-5 text-slate-400" /> {selectedEvent ? selectedEvent.name : 'Select an event'} Monitor
        </h3>
        
        {reportQ.isLoading ? (
          <p className="text-sm text-slate-500">Loading report...</p>
        ) : report.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No scans recorded for this event yet.</p>
        ) : (
          <div className="space-y-4">
            {report.map((r: any) => {
              let deduction = 0;
              if (r.minutes_late > 0 && selectedEvent) deduction = selectedEvent.late_deduction;
              return (
                <div key={r.user_id} className="flex justify-between items-center p-3 border border-slate-100 rounded-lg">
                  <div>
                    <p className="font-bold text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-500">{r.role} · {r.present} present · {r.late} late</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${r.present > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {r.present > 0 ? 'Scanned' : 'Missing'}
                    </span>
                    {deduction > 0 && <p className="text-xs text-red-600 font-bold mt-1">- ₦{deduction.toLocaleString()}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function CreateEventModal({ classes, subjects, onClose, onSuccess, setToast }: any) {
  const [mode, setMode] = useState<'staff' | 'classroom'>('staff');
  
  // Staff mode state
  const [staffForm, setStaffForm] = useState({
    name: '', starts_at: '08:00', ends_at: '17:00', late_after_minutes: 10, late_deduction: 0, absence_deduction: 0,
  });

  // Classroom mode state
  const timetableQ = useQuery({
    queryKey: ['timetable-modal'],
    queryFn: () => getTimetable().then(unwrap), 
    enabled: mode === 'classroom',
  });
  const timetableEntries = arr<any>(timetableQ.data);
  
  const [selectedSlots, setSelectedSlots] = useState<Set<number>>(new Set());
  const [classroomDeductions, setClassroomDeductions] = useState({
    late_after_minutes: 10, late_deduction: 500, absence_deduction: 5000
  });

  const toggleSlot = (id: number) => {
    const next = new Set(selectedSlots);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedSlots(next);
  };

  const save = async () => {
    try {
      let createdEvent = null;
      if (mode === 'staff') {
        if (!staffForm.name) return setToast({type: 'error', msg: 'Enter event name'});
        const res = await postClockinEvent({ ...staffForm, type: 'staff' });
        createdEvent = unwrap(res);
      } else {
        if (selectedSlots.size === 0) return setToast({type: 'error', msg: 'Select at least one timetable slot'});
        
        // Loop through selected slots and create events
        let count = 0;
        for (const slotId of Array.from(selectedSlots)) {
          const slot = timetableEntries.find((t: any) => t.id === slotId);
          if (!slot) continue;
          
          const res = await postClockinEvent({
            name: `${slot.class} - ${slot.subject || 'Class'}`,
            type: 'class',
            timetable_entry_id: slot.id,
            school_class_id: slot.school_class_id,
            subject_id: slot.subject_id,
            starts_at: slot.starts_at,
            ends_at: slot.ends_at,
            late_after_minutes: classroomDeductions.late_after_minutes,
            late_deduction: classroomDeductions.late_deduction,
            absence_deduction: classroomDeductions.absence_deduction,
          });
          // Only grab the first one to auto-show the QR
          if (count === 0) createdEvent = unwrap(res);
          count++;
        }
      }
      onSuccess(createdEvent);
    } catch (e: any) {
      setToast({ type: 'error', msg: e?.response?.data?.message ?? 'Failed to create event' });
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
          <h3 className="font-bold text-slate-900">Create QR Event</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        
        <div className="flex border-b border-slate-200">
          <button onClick={() => setMode('staff')} className={`flex-1 py-3 text-sm font-bold ${mode === 'staff' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500'}`}>Staff / General</button>
          <button onClick={() => setMode('classroom')} className={`flex-1 py-3 text-sm font-bold ${mode === 'classroom' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500'}`}>Classroom (Timetable)</button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          {mode === 'staff' && (
            <>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Event Name *</label>
                <input value={staffForm.name} onChange={e => setStaffForm({...staffForm, name: e.target.value})} placeholder="e.g. Morning Staff Gate" className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Start Time</label><input type="time" value={staffForm.starts_at} onChange={e => setStaffForm({...staffForm, starts_at: e.target.value})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
                <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">End Time</label><input type="time" value={staffForm.ends_at} onChange={e => setStaffForm({...staffForm, ends_at: e.target.value})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Late After (mins)</label><input type="number" value={staffForm.late_after_minutes} onChange={e => setStaffForm({...staffForm, late_after_minutes: Number(e.target.value)})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
                <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Late Deduction (₦)</label><input type="number" value={staffForm.late_deduction} onChange={e => setStaffForm({...staffForm, late_deduction: Number(e.target.value)})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
                <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Absence Deduction (₦)</label><input type="number" value={staffForm.absence_deduction} onChange={e => setStaffForm({...staffForm, absence_deduction: Number(e.target.value)})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
              </div>
            </>
          )}

          {mode === 'classroom' && (
            <>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Late After (mins)</label><input type="number" value={classroomDeductions.late_after_minutes} onChange={e => setClassroomDeductions({...classroomDeductions, late_after_minutes: Number(e.target.value)})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
                <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Late Deduction (₦)</label><input type="number" value={classroomDeductions.late_deduction} onChange={e => setClassroomDeductions({...classroomDeductions, late_deduction: Number(e.target.value)})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
                <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Absence Deduction (₦)</label><input type="number" value={classroomDeductions.absence_deduction} onChange={e => setClassroomDeductions({...classroomDeductions, absence_deduction: Number(e.target.value)})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
              </div>
              
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-600 uppercase">Select Timetable Slots</span>
                  <span className="text-xs text-blue-600 font-bold">{selectedSlots.size} selected</span>
                </div>
                <div className="max-h-60 overflow-y-auto divide-y divide-slate-100">
                  {timetableQ.isLoading ? (
                    <p className="p-4 text-sm text-slate-500">Loading timetable...</p>
                  ) : timetableEntries.length === 0 ? (
                    <p className="p-4 text-sm text-slate-500">No timetable entries found.</p>
                  ) : (
                    timetableEntries.map((slot: any) => (
                      <label key={slot.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" checked={selectedSlots.has(slot.id)} onChange={() => toggleSlot(slot.id)} className="w-4 h-4 text-blue-600 rounded" />
                        <div className="flex-1">
                          <p className="font-bold text-slate-900 text-sm">{slot.class} - {slot.subject || 'Break'}</p>
                          <p className="text-xs text-slate-500">{slot.day} · Period {slot.period} · {slot.starts_at} - {slot.ends_at}</p>
                        </div>
                        <p className="text-xs font-semibold text-slate-700">{slot.teacher || 'No teacher'}</p>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        
        <div className="p-6 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <button onClick={save} className="w-full py-3 bg-[#0f172a] text-white rounded-lg font-bold text-sm shadow-sm hover:bg-slate-800">
            {mode === 'staff' ? 'Create Staff Event' : `Create ${selectedSlots.size} QR Event(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}
